import React, { useEffect, useState, useRef, useCallback } from 'react'
import { getData as apiGetData } from 'api/Api'
import { CancelError } from '@kanbo/api'
import { Modal } from 'components/Modal'
import Loader from 'components/Loader'
import { LeftSidebarView, HeaderButton } from 'components/Sidebar'
import KanBoIcon from 'components/KanBoIcon'
import styled, { css } from 'styled-components'

const StyledLeftSidebarView = styled(LeftSidebarView)`
  ${props => props.maximized ? css`
    width: 100vw !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    border-radius: 0;
  ` : ''}
`

type Props = {
  url: string
  sourceId: string | number
  name: string
  onClose: () => void
}

const MailPreview: React.FC<Props> = ({
  url,
  sourceId,
  name,
  onClose,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [html, setHtml] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [maximized, setMaximized] = useState(false)

  useEffect(() => {
    setIsLoading(true)

    const fetchMailDataPromise = apiGetData('RenderEmail', {
      documentsSourceId: sourceId,
      url
    })

    fetchMailDataPromise.then(({ HtmlBody }) => {
      setHtml(HtmlBody)
      setIsLoading(false)
    }).catch((error) => {
      if (error instanceof CancelError) {
        setHtml(undefined)
        setIsLoading(false)
      } else {
        throw error
      }
    })

    return () => {
      fetchMailDataPromise.cancel()
    }
  }, [url, sourceId])

  useEffect(() => {
    const iframe = iframeRef.current

    if (iframe) {
      if (!html) {
        iframe.style.display = 'none'
        return
      }

      iframe.style.display = 'block'

      const domParser = new DOMParser()
      const htmlDocument = domParser.parseFromString(html, 'text/html')
  
      const scripts = htmlDocument.getElementsByTagName('script')
      let i = scripts.length
      while (i--) {
        scripts[i].parentNode?.removeChild(scripts[i])
      }
  
      if (iframe.contentWindow) {
        iframe.contentWindow.document.open()
        iframe.contentWindow.document.write(htmlDocument.documentElement.innerHTML)
        iframe.contentWindow.document.close()
    
        iframe.height = iframe.contentWindow.document.body.scrollHeight + 'px'
      }
    }
  }, [html])

  const handleMaximizeClick = useCallback(() => {
    setMaximized(maximized => !maximized)
  }, [])

  return (
    <Modal
      onClose={onClose}
      maximized={maximized}
    >
      <StyledLeftSidebarView
        title={name}
        maximized={maximized}
        inlineMenu={
          <HeaderButton
            icon={<KanBoIcon name={maximized ? 'kanbo-16-hide-details' : 'kanbo-16-show-details'} />}
            onClick={handleMaximizeClick}
          />
        }
      >
        {isLoading &&
          <Loader center />
        }
        <iframe
          ref={iframeRef}
          title={name}
          width='100%'
          height='0'
          style={{
            display: 'none',
            border: 'none'
          }}
        />
      </StyledLeftSidebarView>
    </Modal>
  )
}

export default MailPreview
